import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Copy, Check, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { UKGMLogo, UKGM_LINKS } from '@/lib/ui';

export default function DonateModal({ open, onClose }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState('');

  if (!open) return null;

  const copy = (value, key) => {
    navigator.clipboard?.writeText(value);
    setCopied(key);
    toast.success('Copied');
    setTimeout(() => setCopied(''), 2000);
  };

  const rows = [
    { key: 'bank', label: t('donate.bank_label'), value: t('donate.bank_value') },
    { key: 'title', label: t('donate.account_title_label'), value: t('donate.account_title_value') },
    { key: 'no', label: t('donate.account_no_label'), value: t('donate.account_no_value') },
  ];

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="donate-modal">
      <div onClick={e => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden border border-ukgm-gold/30 slide-up">
        <div className="bg-ukgm-blue text-white px-6 py-5 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <UKGMLogo className="w-14 h-14 shrink-0" ring={false} />
            <div>
              <div className="text-ukgm-gold text-xs tracking-widest uppercase font-bold">Donate</div>
              <h3 className="font-serif text-2xl mt-1">{t('donate.title')}</h3>
              <p className="text-sm text-white/80 mt-1 leading-relaxed">{t('donate.subtitle')}</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="close" data-testid="donate-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          <a href={UKGM_LINKS.razorpay} target="_blank" rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 bg-ukgm-gold text-ukgm-blue font-bold px-6 py-3.5 rounded-full hover:bg-ukgm-goldDark gold-glow"
            data-testid="donate-razorpay-btn">
            <ExternalLink className="w-4 h-4" /> Donate via Razorpay (Instant)
          </a>

          <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-ukgm-slate font-bold">
            <span className="flex-1 h-px bg-ukgm-gold/40" /> Or bank transfer <span className="flex-1 h-px bg-ukgm-gold/40" />
          </div>

          <div className="text-xs tracking-widest uppercase text-ukgm-slate font-bold">Bank details</div>
          <div className="rounded-2xl border border-ukgm-gold/30 divide-y divide-ukgm-gold/20 bg-ukgm-cream">
            {rows.map(r => (
              <div key={r.key} className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0">
                  <div className="text-[10px] tracking-widest uppercase text-ukgm-slate font-bold">{r.label}</div>
                  <div className="text-sm font-semibold text-ukgm-blue truncate mt-0.5" data-testid={`bank-${r.key}`}>{r.value}</div>
                </div>
                <button onClick={() => copy(r.value, r.key)} className="text-ukgm-blue hover:text-ukgm-gold p-2" data-testid={`copy-${r.key}`} aria-label="copy">
                  {copied === r.key ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            ))}
          </div>

          <p className="text-xs text-ukgm-slate leading-relaxed">{t('donate.share_note')}</p>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <a href="mailto:ukgm.trust@gmail.com" className="bg-ukgm-gold text-ukgm-blue font-bold px-4 py-3 rounded-full text-center hover:bg-ukgm-goldDark" data-testid="donate-email-btn">
              ukgm.trust@gmail.com
            </a>
            <a href="tel:+917447261155" className="border border-ukgm-gold/40 text-ukgm-blue font-bold px-4 py-3 rounded-full text-center hover:bg-ukgm-goldLight" data-testid="donate-phone-btn">
              +91 74472 61155
            </a>
          </div>

          <button onClick={onClose} className="w-full mt-2 px-6 py-2.5 rounded-full text-sm text-ukgm-slate hover:text-ukgm-blue">
            {t('donate.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
